import React, { useState, useRef } from 'react';
import usePageMeta from '../hooks/usePageMeta';
import './Contact.css';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^[0-9()+\-.\s]{7,20}$/;
const COOLDOWN_MS = 10000;

function Contact() {
  usePageMeta({
    title: 'Contact Us',
    description: 'Get in touch with Heaven Sent Promotions & Apparel at (210) 662-7958 or visit us at 900 Basse Rd, San Antonio, TX 78212. Family-run, locally trusted since 2007.',
    path: '/contact',
  });
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('');
  const lastSubmit = useRef(0);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.length > 100) errs.name = 'Please enter your name (max 100 characters)';
    if (!EMAIL_REGEX.test(form.email)) errs.email = 'Please enter a valid email address';
    if (form.phone && !PHONE_REGEX.test(form.phone)) errs.phone = 'Please enter a valid phone number';
    if (!form.subject.trim() || form.subject.length > 200) errs.subject = 'Please enter a subject (max 200 characters)';
    if (!form.message.trim() || form.message.length > 2000) errs.message = 'Please enter a message (max 2000 characters)';
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (honeypot) return;

    const now = Date.now();
    if (now - lastSubmit.current < COOLDOWN_MS) {
      setStatus('cooldown');
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setStatus('sending');
    lastSubmit.current = now;

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: honeypot,
      };

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
      setErrors({});
    } catch {
      setStatus('error');
    }
  };

  return (
    <main className="contact-page">
      <section
        className="page-hero"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(20, 12, 16, 0.55) 0%, rgba(40, 18, 28, 0.35) 60%, rgba(60, 25, 40, 0.45) 100%), url('/images/contact-banner.webp')",
        }}
      >
        <h1 className="script-font">Contact Us</h1>
        <p>We'd love to hear from you</p>
      </section>

      <section className="contact-section">
        <div className="contact-grid">
          <div className="contact-info">
            <h2>Get In Touch</h2>
            <p>Have a question or need help with an order? Reach out and we'll get back to you as soon as possible.</p>

            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">&#128222;</div>
                <div>
                  <strong>Phone</strong>
                  <a href="tel:2106627958">(210) 662-7958</a>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">&#128231;</div>
                <div>
                  <strong>Email</strong>
                  <a href="mailto:susan@heavensentpromos.com">susan@heavensentpromos.com</a>
                  <a href="mailto:sarita@heavensentpromos.com">sarita@heavensentpromos.com</a>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">&#128205;</div>
                <div>
                  <strong>Location</strong>
                  <span>900 Basse Rd, San Antonio, TX 78212</span>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">&#128337;</div>
                <div>
                  <strong>Hours</strong>
                  <span>Mon - Fri: 9am - 6pm</span>
                </div>
              </div>
            </div>

            <div className="contact-social">
              <a href="https://www.facebook.com/angelwings1234/" target="_blank" rel="noopener noreferrer">Facebook</a>
              <a href="https://www.instagram.com/heavensentpromosandapparel/" target="_blank" rel="noopener noreferrer">Instagram</a>
              <a href="https://www.linkedin.com/company/heaven-sent-promotions-apparel-llc" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} autoComplete="off">
            {/* Honeypot - hidden from real users. Name is intentionally
                obscure so password managers and browser autofill don't
                populate it — a filled honeypot silently drops the
                submission, which caused every submit to appear as a
                no-op when 1Password/Chrome autofilled the previous
                `website` field. */}
            <input
              type="text"
              name="hs_contact_url"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
            />
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" name="name" value={form.name} onChange={handleChange} maxLength={100} required />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} maxLength={100} required />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} maxLength={20} />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <input type="text" name="subject" value={form.subject} onChange={handleChange} maxLength={200} required />
                {errors.subject && <span className="field-error">{errors.subject}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea name="message" value={form.message} onChange={handleChange} rows="5" maxLength={2000} required></textarea>
              {errors.message && <span className="field-error">{errors.message}</span>}
            </div>
            <button type="submit" className="btn-pink" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </button>
            {status === 'success' && <p className="form-success">Message sent! We'll get back to you soon.</p>}
            {status === 'error' && <p className="form-error">Something went wrong. Please try again or call us directly.</p>}
            {status === 'cooldown' && <p className="form-error">Please wait a moment before submitting again.</p>}
          </form>
        </div>
      </section>
    </main>
  );
}

export default Contact;
